import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Space, Select, Tag, Statistic, Card, Row, Col } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getAuditLogs,
  getAuditSummary,
  type AuditLog,
  type AuditLogFilters,
  type AuditSummary,
} from '@/api/auditApi';
import AuditNav from './AuditNav';

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
};

export default function AuditLogListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [summary, setSummary] = useState<AuditSummary | null>(null);

  useEffect(() => {
    getAuditSummary().then(setSummary).catch(() => {});
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof AuditLogFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getAuditLogs({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const columns: ColumnsType<AuditLog> = useMemo(
    () => [
      {
        title: t('audit.field.timestamp'),
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 170,
        sorter: true,
        render: (ts: string) => new Date(ts).toLocaleString(),
      },
      {
        title: t('audit.field.user_id'),
        dataIndex: 'user_id',
        key: 'user_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('audit.field.action'),
        dataIndex: 'action',
        key: 'action',
        width: 100,
        render: (action: string) => (
          <Tag color={ACTION_COLORS[action] ?? 'default'}>
            {t(`audit.action_options.${action}`, action)}
          </Tag>
        ),
      },
      {
        title: t('audit.field.resource'),
        dataIndex: 'resource',
        key: 'resource',
        width: 150,
        ellipsis: true,
      },
      {
        title: t('audit.field.resource_id'),
        dataIndex: 'resource_id',
        key: 'resource_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('audit.field.ip_address'),
        dataIndex: 'ip_address',
        key: 'ip_address',
        width: 130,
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 80,
        render: (_: unknown, record: AuditLog) => (
          <a onClick={() => navigate(`/audit/logs/${record.id}`)}>
            <EyeOutlined />
          </a>
        ),
      },
    ],
    [t, navigate]
  );

  const actionOptions = useMemo(
    () => [
      { value: '', label: t('audit.filter.all') },
      { value: 'create', label: t('audit.action_options.create') },
      { value: 'update', label: t('audit.action_options.update') },
      { value: 'delete', label: t('audit.action_options.delete') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('audit.filter.action')}
        style={{ width: 120 }}
        allowClear
        options={actionOptions}
        onChange={(val) => handleFilterChange('action', val || undefined)}
      />
      <Select
        placeholder={t('audit.filter.resource')}
        style={{ width: 160 }}
        allowClear
        showSearch
        onChange={(val) => handleFilterChange('resource', val || undefined)}
        options={
          summary?.by_resource.map((r) => ({ value: r.resource, label: r.resource })) ?? []
        }
      />
    </Space>
  );

  return (
    <div>
      <AuditNav />
      <h2>{t('audit.list_title')}</h2>

      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card>
              <Statistic title={t('audit.summary.total')} value={summary.total} />
            </Card>
          </Col>
          {summary.by_action.slice(0, 3).map((item) => (
            <Col span={5} key={item.action}>
              <Card>
                <Statistic
                  title={t(`audit.action_options.${item.action}`, item.action)}
                  value={item.count}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <DataGrid<AuditLog>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('audit.search_placeholder')}
        defaultSortField="timestamp"
        defaultSortDescending
        toolbar={filterBar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/audit/logs/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
